// frontend/src/features/livestock/EditLivestockModal.tsx
/**
 * Modal for editing existing livestock entries.
 * Fullscreen on mobile for better UX.
 */
import { useState, useEffect } from 'react';
import {
  Modal,
  TextInput,
  NumberInput,
  Select,
  Button,
  Stack,
  Group,
  Checkbox,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useUpdateLivestock } from '@/hooks/useLivestock';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CURRENCIES, STATUS_OPTIONS } from '@/types/livestock';
import { CORAL_CATEGORY_OPTIONS, getSubcategoriesForCategory } from '@/data/coralCategories';
import type { Livestock, Currency, LivestockStatus } from '@/types/livestock';

interface EditLivestockModalProps {
  opened: boolean;
  onClose: () => void;
  livestock: Livestock | null;
}

export function EditLivestockModal({
  opened,
  onClose,
  livestock,
}: EditLivestockModalProps) {
  const isMobile = useIsMobile();
  const [commonName, setCommonName] = useState('');
  const [scientificName, setScientificName] = useState('');
  const [category, setCategory] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [acquisitionDate, setAcquisitionDate] = useState<string | null>(null);
  const [deceasedDate, setDeceasedDate] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [vendor, setVendor] = useState('');
  const [pricePaid, setPricePaid] = useState<number | string>('');
  const [currency, setCurrency] = useState<string>('USD');
  const [tankBred, setTankBred] = useState(false);
  const [status, setStatus] = useState<string>('alive');
  const updateMutation = useUpdateLivestock();

  // When status changes to deceased, auto-set deceased date to today if not already set
  const handleStatusChange = (newStatus: string | null) => {
    const s = newStatus || 'alive';
    setStatus(s);
    if (s === 'deceased' && !deceasedDate) {
      setDeceasedDate(new Date().toISOString().slice(0, 10));
    }
  };

  // Populate form when livestock changes
  useEffect(() => {
    if (livestock) {
      setCommonName(livestock.common_name);
      setScientificName(livestock.scientific_name || '');
      setCategory(livestock.category || '');
      setSubcategory(livestock.subcategory || '');
      setAcquisitionDate(livestock.acquisition_date);
      setDeceasedDate(livestock.deceased_date ?? null);
      setQuantity(livestock.quantity);
      setVendor(livestock.vendor || '');
      setPricePaid(livestock.price_paid ?? '');
      setCurrency(livestock.currency);
      setTankBred(livestock.tank_bred);
      setStatus(livestock.status);
    }
  }, [livestock]);

  const handleSubmit = () => {
    if (!livestock || !commonName) return;

    updateMutation.mutate(
      {
        id: livestock.id,
        data: {
          common_name: commonName,
          scientific_name: scientificName || null,
          category: category || null,
          subcategory: subcategory || null,
          acquisition_date: acquisitionDate || undefined,
          deceased_date: status === 'deceased' ? deceasedDate : null,
          quantity,
          vendor: vendor || null,
          price_paid: typeof pricePaid === 'number' ? pricePaid : null,
          currency: currency as Currency,
          tank_bred: tankBred,
          status: status as LivestockStatus,
        },
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit Livestock"
      size="md"
      fullScreen={isMobile}
      transitionProps={isMobile ? { transition: 'slide-up' } : undefined}
    >
      <Stack>
        <TextInput
          label="Common Name"
          value={commonName}
          onChange={(e) => setCommonName(e.currentTarget.value)}
          required
        />

        <TextInput
          label="Scientific Name"
          value={scientificName}
          onChange={(e) => setScientificName(e.currentTarget.value)}
        />

        {livestock?.livestock_type === 'coral' ? (
          <>
            <Select
              label="Category"
              placeholder="Select category"
              data={CORAL_CATEGORY_OPTIONS}
              value={category || null}
              onChange={(val) => {
                setCategory(val || '');
                setSubcategory('');
              }}
              searchable
              clearable
            />
            {category && (
              <Select
                label="Subcategory"
                placeholder="Select subcategory"
                data={getSubcategoriesForCategory(category)}
                value={subcategory || null}
                onChange={(val) => setSubcategory(val || '')}
                searchable
                clearable
              />
            )}
          </>
        ) : (
          <TextInput
            label="Category"
            placeholder={
              livestock?.livestock_type === 'fish'
                ? 'e.g., Wrasse, Clownfish, Tang'
                : livestock?.livestock_type === 'invertebrate'
                  ? 'e.g., Shrimp, Crab, Snail'
                  : 'Category'
            }
            value={category}
            onChange={(e) => setCategory(e.currentTarget.value)}
          />
        )}

        <DateInput
          label="Acquisition Date"
          value={acquisitionDate}
          onChange={setAcquisitionDate}
        />

        <NumberInput
          label="Quantity"
          value={quantity}
          onChange={(v) => setQuantity(typeof v === 'number' ? v : 1)}
          min={1}
        />

        <Select
          label="Status"
          data={STATUS_OPTIONS.map((s) => ({ value: s.value, label: s.label }))}
          value={status}
          onChange={handleStatusChange}
        />

        {status === 'deceased' && (
          <DateInput
            label="Deceased Date"
            value={deceasedDate}
            onChange={setDeceasedDate}
            maxDate={new Date()}
          />
        )}

        <TextInput
          label="Vendor"
          placeholder="e.g., LiveAquaria, Local Fish Store"
          value={vendor}
          onChange={(e) => setVendor(e.currentTarget.value)}
        />

        <Checkbox
          label="Tank bred"
          checked={tankBred}
          onChange={(e) => setTankBred(e.currentTarget.checked)}
        />

        <Group grow>
          <NumberInput
            label="Price Paid"
            placeholder="0.00"
            value={pricePaid}
            onChange={setPricePaid}
            decimalScale={2}
            min={0}
          />
          <Select
            label="Currency"
            data={CURRENCIES.map((c) => ({
              value: c.code,
              label: `${c.symbol} ${c.code}`,
            }))}
            value={currency}
            onChange={(v) => setCurrency(v || 'USD')}
          />
        </Group>

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={updateMutation.isPending}
            disabled={!commonName}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
